// Generated by Claude Code
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CircleCheck, CircleX } from 'lucide-react'
import { cn } from '#core/utils/cn.ts'
import type CompanyAccessRequest from '#core/entities/auth/companies/access-requests/CompanyAccessRequest.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { SettingsTable } from '#components/domain/operations/settings/users-teams/settings-table.tsx'
import type { AccessRequestsState } from './access-requests-state.ts'

interface AccessRequestsTabProps {
  state: AccessRequestsState
  searchQuery: string
  /** The company's members, to name who decided a request. */
  users: UserCompany[]
  roles: RoleDetail[]
  /** Called after an approval, which adds a member. */
  onApproved: () => void
}

/** People asking to join the company by signing in with SSO; approvers decide on each one here. */
export function AccessRequestsTab({ state, searchQuery, users, roles, onApproved }: AccessRequestsTabProps) {
  const { t } = useTranslation('settings')

  useEffect(() => {
    void state.ensureLoaded()
  }, [state])

  const requests = state.filtered(searchQuery)
  const decision = state.decision
  const deciderName = (id: string) => users.find(u => u.id === id)?.displayName ?? id.slice(0, 8)

  return (
    <>
      {state.loading && state.requests.length === 0 ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : state.loadError ? (
        <div className="alert alert-danger" role="alert">{t('usersTeams.loadError')}</div>
      ) : (
        <SettingsTable
          isEmpty={requests.length === 0}
          emptyLabel={t('usersTeams.noAccessRequests')}
          countLabel={t('usersTeams.accessRequestsShowing', { count: requests.length, total: state.requests.length })}
          headers={
            <>
              <th>{t('usersTeams.accessRequests.requester')}</th>
              <th>{t('usersTeams.accessRequests.requestedOn')}</th>
              <th>{t('shared.fields.status')}</th>
              <th>{t('usersTeams.accessRequests.decidedBy')}</th>
              <th className="text-end">{t('shared.fields.actions')}</th>
            </>
          }
        >
          {requests.map(request => (
            <AccessRequestRow
              key={request.id}
              request={request}
              deciderName={request.decided_by_user_id ? deciderName(request.decided_by_user_id) : null}
              onApprove={() => state.requestDecision('approve', request.id)}
              onReject={() => state.requestDecision('reject', request.id)}
            />
          ))}
        </SettingsTable>
      )}

      <ConfirmModal
        isOpen={decision !== null}
        title={decision?.type === 'approve' ? t('usersTeams.accessRequests.approveModal.title') : t('usersTeams.accessRequests.rejectConfirm.title')}
        message={decision && (
          <>
            <Trans
              i18nKey={decision.type === 'approve' ? 'usersTeams.accessRequests.approveModal.message' : 'usersTeams.accessRequests.rejectConfirm.message'}
              ns="settings"
              values={{ name: decision.name }}
            />
            {decision.type === 'approve' && (
              <>
                <label htmlFor="access-request-role" className="form-label d-block mt-3 mb-1">{t('shared.fields.role')}</label>
                <select
                  id="access-request-role"
                  className="form-select"
                  value={state.roleId}
                  onChange={e => { state.roleId = e.target.value }}
                >
                  <option value="">{t('usersTeams.accessRequests.approveModal.noRole')}</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
                <span className="form-text d-block">{t('usersTeams.accessRequests.approveModal.roleHint')}</span>
              </>
            )}
          </>
        )}
        confirmLabel={decision?.type === 'approve' ? t('usersTeams.accessRequests.approve') : t('usersTeams.accessRequests.reject')}
        cancelLabel={t('shared.actions.cancel')}
        genericErrorLabel={t('usersTeams.accessRequests.toast.error')}
        variant={decision?.type === 'approve' ? 'primary' : 'danger'}
        onClose={() => state.cancelDecision()}
        onConfirm={() => state.executeDecision(onApproved)}
      />
    </>
  )
}

interface AccessRequestRowProps {
  request: CompanyAccessRequest
  deciderName: string | null
  onApprove: () => void
  onReject: () => void
}

function AccessRequestRow({ request, deciderName, onApprove, onReject }: AccessRequestRowProps) {
  const { t } = useTranslation('settings')

  return (
    <tr>
      <td>
        <div className="fw-medium">{request.displayName}</div>
        {request.displayName !== request.email && <div className="text-secondary small">{request.email}</div>}
      </td>
      <td className="text-secondary">{request.formatDateTime(request.created_at)}</td>
      <td><span className={cn('badge', request.statusBadgeClass)}>{t(request.statusKey)}</span></td>
      <td>
        {request.isPending ? (
          <span className="text-secondary">—</span>
        ) : (
          <>
            <div>{deciderName ?? '—'}</div>
            <div className="text-secondary small">{request.formatDateTime(request.decided_at)}</div>
          </>
        )}
      </td>
      <td className="text-end text-nowrap">
        {request.isPending && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-icon p-1"
              title={t('usersTeams.accessRequests.approve')}
              aria-label={t('usersTeams.accessRequests.approve')}
              onClick={onApprove}
            >
              <CircleCheck size={16} className="text-success" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-icon p-1 ms-1"
              title={t('usersTeams.accessRequests.reject')}
              aria-label={t('usersTeams.accessRequests.reject')}
              onClick={onReject}
            >
              <CircleX size={16} className="text-danger" />
            </button>
          </>
        )}
      </td>
    </tr>
  )
}
